import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';

/**
 * Caution band drawn when the live candle is trading near a round price level.
 *
 * Two grids: hundreds carry the label, fifties are outline only.
 *
 * NOT every integer. On MNQ at ~29,500 every price is within 15 points of SOME
 * integer, so an "any whole number" reading would leave the band permanently on
 * and say nothing at all. The hundreds are the coarsest grid that still switches
 * off, and the levels that actually collect resting orders; the fifties ride
 * along as a minor tier for the levels in between.
 *
 * At ±15 the hundreds cover 30% of the range and both grids together cover 60%,
 * with only 20 clear points between a hundred and its neighbouring fifty. Both
 * tiers are therefore styled quietly — a marker visible more than half the time
 * is context, and drawing it like an alarm only teaches the eye to skip it.
 *
 * The band spans the full caution zone (level ± {@link BAND_POINTS}) rather
 * than the candle's own range, so it shows where the danger IS, not merely
 * where price is, and you can see how deep into the zone price has travelled.
 *
 * It begins at the RIGHT EDGE of the live candle — not its centre.
 * `timeToCoordinate` returns a candle's midpoint, so drawing from that value
 * would lay the band over the right half of the live candle and hide the very
 * price action being warned about. Half a bar is added to clear it.
 */

/** Grid of levels that count as "round". */
const LEVEL_STEP = 100;

/**
 * Half-height of the caution zone: the band covers level ± this.
 *
 * At 15 the hundreds alone cover 30% of the range and the two grids together
 * cover 60% — the bands very nearly touch, leaving only 20 clear points
 * between a hundred and the fifty either side of it. That duty cycle is
 * exactly why the styling below is restrained: something on screen more than
 * half the time has to read as context, not as an alarm.
 */
const BAND_POINTS = 15;

/** How far right of the live candle the band extends. */
const EXTEND_BARS = 10;

/**
 * Two tiers, because they are not the same warning.
 *
 * A hundred is a real magnet — resting orders collect there and it fires about
 * 47% of the session. A fifty is a weaker level, and drawing it with the same
 * weight would flatten the distinction: the two grids together cover ~60% of
 * the range, so a single style would leave a loud band on for most of the day
 * and the hundreds would stop standing out. The minor tier is therefore quiet
 * enough to read as context rather than a warning — no fill, a dashed edge,
 * and no label.
 */
export type LevelTier = 'major' | 'minor';

const STYLE: Record<LevelTier, {
  fill: string | null;
  border: string;
  label: string | null;
  dash: number[];
  /** Prefix before the level, e.g. "⚠ WARNING". Null keeps the tier quiet. */
  warn: string | null;
}> = {
  major: {
    // Toned down with the band widened to ±15. The old values were sized for a
    // marker that appeared 20% of the time; at 30% the same weight reads as a
    // permanent yellow wash over the working area of the chart.
    fill: 'rgba(234, 179, 8, 0.06)',
    border: 'rgba(234, 179, 8, 0.30)',
    label: 'rgba(234, 179, 8, 0.70)',
    dash: [4, 4],
    warn: '⚠ CAUTION',
  },
  minor: {
    // Subtle means quieter than the hundreds, NOT invisible — which is what
    // 0.14 with no fill amounted to: two 1px dashed lines on a dark chart that
    // could not be seen at all. It now carries a faint fill so the zone reads
    // as a band rather than as a pair of stray lines, and a border you can
    // actually find, while staying clearly the lesser of the two tiers.
    fill: 'rgba(234, 179, 8, 0.03)',
    border: 'rgba(234, 179, 8, 0.28)',
    // Labelled, but only with the level. The "⚠ CAUTION" prefix stays on the
    // hundreds: that prefix is what separates a warning from a note, and
    // putting it on both would make the distinction meaningless.
    label: 'rgba(234, 179, 8, 0.45)',
    dash: [2, 4],
    warn: null,
  },
};

export interface RoundLevelWarningData {
  /** Live candle's bar time in SECONDS, or null when there is no live bar. */
  time: number | null;
  /** Current price. `null` suppresses the band rather than drawing it at 0. */
  price: number | null;
}

interface RendererItem {
  x1: number;
  x2: number;
  yTop: number;
  yBottom: number;
  label: string;
  tier: LevelTier;
}

/**
 * The round level `price` is within {@link BAND_POINTS} of, or null.
 *
 * Exported for tests: this is the whole rule, and it is much easier to pin down
 * here than through a canvas.
 */
export function nearestRoundLevel(
  price: number,
  step: number = LEVEL_STEP,
  band: number = BAND_POINTS
): number | null {
  if (!Number.isFinite(price)) return null;
  const level = Math.round(price / step) * step;
  return Math.abs(price - level) <= band ? level : null;
}

/**
 * The level to warn about and how loudly, or null when price is between both
 * grids.
 *
 * The hundred is checked FIRST and wins outright. Every hundred is also a
 * multiple of fifty, so testing the fifty grid first would classify 29500 as
 * minor and the major tier would never render at all.
 */
export function levelWarning(price: number): { level: number; tier: LevelTier } | null {
  const major = nearestRoundLevel(price, LEVEL_STEP, BAND_POINTS);
  if (major !== null) return { level: major, tier: 'major' };
  const minor = nearestRoundLevel(price, LEVEL_STEP / 2, BAND_POINTS);
  if (minor !== null) return { level: minor, tier: 'minor' };
  return null;
}

class RoundLevelWarningRenderer implements IPrimitivePaneRenderer {
  private _item: RendererItem | null = null;

  update(item: RendererItem | null): void {
    this._item = item;
  }

  draw(target: any): void {
    const item = this._item;
    if (!item) return;

    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const h = scope.horizontalPixelRatio as number;
      const v = scope.verticalPixelRatio as number;

      // A bad frame must never take down the chart's render loop.
      try {
        ctx.save();
        ctx.shadowBlur = 0;

        const x = item.x1 * h;
        const w = (item.x2 - item.x1) * h;
        const y = Math.min(item.yTop, item.yBottom) * v;
        const hh = Math.abs(item.yBottom - item.yTop) * v;

        const style = STYLE[item.tier];

        if (style.fill) {
          ctx.fillStyle = style.fill;
          ctx.fillRect(x, y, w, hh);
        }

        // Top and bottom edges, so the zone's boundaries stay readable against
        // candles that may sit inside it.
        ctx.strokeStyle = style.border;
        ctx.lineWidth = Math.max(1, h);
        ctx.setLineDash(style.dash.map((d) => d * h));
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + w, y);
        ctx.moveTo(x, y + hh);
        ctx.lineTo(x + w, y + hh);
        ctx.stroke();
        ctx.setLineDash([]);

        if (style.label) {
          const fontSize = Math.round(9 * h);
          ctx.font = `700 ${fontSize}px sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = style.label;

          const text = style.warn ? `${style.warn} ${item.label}` : item.label;
          const tx = x + 4 * h;
          const ty = y + hh / 2;

          // Dark plate behind the text. The band is only 20 points tall, so on
          // a zoomed-in chart candles run straight through it and the label
          // would otherwise sit unreadable on top of a wick.
          const tw = ctx.measureText(text).width;
          const pad = 3 * h;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.40)';
          ctx.fillRect(tx - pad, ty - fontSize / 2 - pad, tw + pad * 2, fontSize + pad * 2);

          ctx.fillStyle = style.label;
          ctx.fillText(text, tx, ty);
        }

        ctx.restore();
      } catch {
        // drop the frame silently
      }
    });
  }
}

class RoundLevelWarningPaneView implements IPrimitivePaneView {
  private _renderer = new RoundLevelWarningRenderer();

  constructor(private _source: RoundLevelWarningPrimitive) {}

  zOrder(): PrimitivePaneViewZOrder {
    // Below the top-most labels but above the grid, so candles inside the zone
    // stay legible through the translucent fill.
    return 'normal';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererItem());
    return this._renderer;
  }
}

export class RoundLevelWarningPrimitive implements ISeriesPrimitive<Time> {
  private _paneView: RoundLevelWarningPaneView;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _requestUpdate?: () => void;
  private _data: RoundLevelWarningData = { time: null, price: null };

  constructor() {
    this._paneView = new RoundLevelWarningPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Coordinates are recomputed in getRendererItem on every frame, so a pan or
    // a price-scale change is picked up without any subscription of its own.
  }

  setData(data: RoundLevelWarningData): void {
    this._data = data;
    this._requestUpdate?.();
  }

  /**
   * Screen geometry for this frame, or null when nothing should be drawn.
   *
   * Null rather than a zero-size rect in every "cannot draw" case — no live
   * bar, no price, price not near a level, coordinates off-screen — because a
   * degenerate rectangle still paints a 1px artefact at the top of the pane.
   */
  getRendererItem(): RendererItem | null {
    if (!this._series || !this._chart) return null;
    const { time, price } = this._data;
    if (time === null || price === null) return null;

    const warning = levelWarning(price);
    if (warning === null) return null;
    const { level, tier } = warning;

    const timeScale = this._chart.timeScale();
    const centreX = timeScale.timeToCoordinate(time as Time);
    if (centreX === null) return null;

    const barSpacing = timeScale.options().barSpacing;
    // timeToCoordinate is the candle's CENTRE; start at its right edge so the
    // band never covers the live candle it is warning about.
    const x1 = centreX + barSpacing / 2;
    const x2 = x1 + EXTEND_BARS * barSpacing;

    const yTop = this._series.priceToCoordinate(level + BAND_POINTS);
    const yBottom = this._series.priceToCoordinate(level - BAND_POINTS);
    if (yTop === null || yBottom === null) return null;

    return { x1, x2, yTop, yBottom, label: `${level}`, tier };
  }
}

export function createRoundLevelWarningPrimitive(): RoundLevelWarningPrimitive {
  return new RoundLevelWarningPrimitive();
}
